/**
 * PrivacyConsentGate.tsx — richiesta di accettazione dell'Informativa Privacy.
 *
 * GDPR (Ott 2026): Rombo mostra la "versione privacy" nell'anagrafica socio;
 * qui la richiesta appare AL PRIMO ACCESSO quando l'utente non ha ancora
 * accettato la versione corrente dell'informativa (nuovi utenti Google,
 * utenti esistenti, oppure dopo un aggiornamento dell'informativa).
 *
 * Comportamento:
 * - non blocca l'app: "Più tardi" chiude il pannello per la sessione corrente
 *   (riappare al prossimo avvio); l'accettazione resta tracciata quando data;
 * - la versione accettata è SEMPRE quella corrente del server (il client non
 *   sceglie nulla);
 * - "Leggi l'informativa" apre /privacy.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import useAuthStore from "@/store/useAuthStore";
import { getPrivacyConsent, acceptPrivacyConsent } from "@/api";
import type { PrivacyConsentStatus } from "@/api";

/** Evento emesso dopo un'accettazione per allineare tutte le superfici aperte. */
export const PRIVACY_CONSENT_EVENT = "rc:privacy-consent-updated";

const PrivacyConsentGate: React.FC = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [status, setStatus] = useState<PrivacyConsentStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!accessToken) return;
    let alive = true;
    setDismissed(false);
    getPrivacyConsent()
      .then((s) => {
        if (alive) setStatus(s);
      })
      .catch(() => {
        /* silenzioso: la richiesta non deve mai bloccare l'app */
      });
    return () => {
      alive = false;
    };
  }, [accessToken]);

  if (!accessToken || !status?.needsAcceptance || dismissed) return null;

  const accept = async () => {
    setSaving(true);
    setError("");
    try {
      const next = await acceptPrivacyConsent();
      setStatus(next);
      window.dispatchEvent(new Event(PRIVACY_CONSENT_EVENT));
    } catch {
      setError("Accettazione non registrata: riprova tra poco.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-gate-title"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900 p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/15 border border-brand-500/25">
            <ShieldCheck size={20} className="text-brand-400" />
          </div>
          <h2 id="privacy-gate-title" className="text-base font-bold text-white">
            Informativa Privacy aggiornata
          </h2>
        </div>

        <p className="text-sm text-neutral-300 leading-relaxed">
          Abbiamo aggiornato l'Informativa Privacy (versione{" "}
          <span className="font-semibold text-white">{status.currentVersion}</span>
          ). Conferma di averla letta per continuare a usare RideConnect.
        </p>

        {error && (
          <p role="alert" className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={accept}
            disabled={saving}
            className="w-full rounded-xl bg-brand-500 py-3 font-bold text-white transition-colors hover:bg-brand-400 disabled:opacity-50"
          >
            {saving ? "Registrazione…" : "Accetto l'Informativa Privacy"}
          </button>
          <Link
            to="/privacy"
            onClick={() => setDismissed(true)}
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-3 text-center text-sm font-semibold text-neutral-300 transition-colors hover:bg-white/[0.07] hover:text-white"
          >
            Leggi l'informativa
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="w-full py-2 text-xs font-medium text-neutral-500 transition-colors hover:text-neutral-300"
          >
            Più tardi
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentGate;
